import { useEffect, useState } from 'react';
import { RemoteParticipant } from 'twilio-video';
import useDominantSpeaker from '../useDominantSpeaker/useDominantSpeaker';
import useVideoContext from '../useVideoContext/useVideoContext';

const getParticipantId = (participant: RemoteParticipant) => participant.sid;

const orderParticipants = (participants: RemoteParticipant[], dominantSpeaker: RemoteParticipant | null) => {
  const uniqueParticipants = Array.from(
    new Map(participants.map(participant => [getParticipantId(participant), participant])).values()
  );

  if (!dominantSpeaker) {
    return uniqueParticipants;
  }

  const dominantSpeakerId = getParticipantId(dominantSpeaker);
  const activeDominantSpeaker = uniqueParticipants.find(
    participant => getParticipantId(participant) === dominantSpeakerId
  );

  if (!activeDominantSpeaker) {
    return uniqueParticipants;
  }

  return [
    activeDominantSpeaker,
    ...uniqueParticipants.filter(participant => getParticipantId(participant) !== dominantSpeakerId),
  ];
};

export default function useParticipants() {
  const { room } = useVideoContext();
  const dominantSpeaker = useDominantSpeaker();
  const [participants, setParticipants] = useState<RemoteParticipant[]>(() =>
    orderParticipants(Array.from(room?.participants.values() ?? []), dominantSpeaker)
  );

  // When the dominant speaker changes, they are moved to the front of the participants array.
  // This means that the most recent dominant speakers will always be near the top of the
  // ParticipantStrip component.
  useEffect(() => {
    setParticipants(orderParticipants(Array.from(room?.participants.values() ?? []), dominantSpeaker));
  }, [room, dominantSpeaker]);

  useEffect(() => {
    if (!room) {
      return;
    }

    const participantConnected = (participant: RemoteParticipant) =>
      setParticipants(prevParticipants => orderParticipants([...prevParticipants, participant], dominantSpeaker));
    const participantDisconnected = (participant: RemoteParticipant) =>
      setParticipants(prevParticipants =>
        orderParticipants(
          prevParticipants.filter(p => getParticipantId(p) !== getParticipantId(participant)),
          dominantSpeaker
        )
      );

    room.on('participantConnected', participantConnected);
    room.on('participantDisconnected', participantDisconnected);

    return () => {
      room.off('participantConnected', participantConnected);
      room.off('participantDisconnected', participantDisconnected);
    };
  }, [room, dominantSpeaker]);

  return participants;
}
